import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Check, X, Truck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  "Pending",
  "Ready to Ship",
  "In Transit",
  "Out for Delivery",
  "Delivered",
  "Returned",
  "Cancelled",
];

const STATUS_BADGE_COLOR = {
  "Pending": "bg-zinc-100 text-zinc-700 border-zinc-300",
  "Ready to Ship": "bg-yellow-100 text-yellow-800 border-yellow-300",
  "In Transit": "bg-blue-100 text-blue-800 border-blue-300",
  "Out for Delivery": "bg-purple-100 text-purple-800 border-purple-300",
  "Delivered": "bg-green-100 text-green-800 border-green-300",
  "Returned": "bg-red-100 text-red-800 border-red-300",
  "Cancelled": "bg-zinc-200 text-zinc-600 border-zinc-400 line-through",
};

const EMPTY_ROW = {
  tracking_number: "",
  status: "Pending",
  artwork: "",
  vendor_name: "",
  ship_date: "",
  delivery_date: "",
  order_number: "",
  event_code: "",
};

export function ShipmentsTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);  // 'NEW' or actual id
  const [editValues, setEditValues] = useState(EMPTY_ROW);

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/shipments`);
      setRows(r.data);
    } catch (e) {
      toast.error("Failed to load shipments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchShipments(); }, [fetchShipments]);

  const startNew = () => {
    setEditingId("NEW");
    setEditValues({ ...EMPTY_ROW });
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditValues({
      tracking_number: row.tracking_number || "",
      status: row.status || "Pending",
      artwork: row.artwork || "",
      vendor_name: row.vendor_name || "",
      ship_date: row.ship_date ? row.ship_date.slice(0, 10) : "",
      delivery_date: row.delivery_date ? row.delivery_date.slice(0, 10) : "",
      order_number: row.order_number || "",
      event_code: row.event_code || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues(EMPTY_ROW);
  };

  const save = async () => {
    const payload = {
      ...editValues,
      ship_date: editValues.ship_date || null,
      delivery_date: editValues.delivery_date || null,
    };
    try {
      if (editingId === "NEW") {
        await axios.post(`${API}/shipments`, payload);
        toast.success("Shipment added");
      } else {
        await axios.put(`${API}/shipments/${editingId}`, payload);
        toast.success("Shipment updated");
      }
      cancelEdit();
      await fetchShipments();
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const deleteRow = async (id, tracking) => {
    if (!window.confirm(`Delete shipment${tracking ? ` ${tracking}` : ""}?`)) return;
    try {
      await axios.delete(`${API}/shipments/${id}`);
      toast.success("Deleted");
      await fetchShipments();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const renderEditRow = (id, key) => (
    <TableRow key={key} data-testid={`ship-edit-row-${id}`}>
      <TableCell>
        <Input value={editValues.tracking_number} onChange={(e) => setEditValues({ ...editValues, tracking_number: e.target.value })} placeholder="1Z..." className="h-8 rounded-none border-2" />
      </TableCell>
      <TableCell>
        <Select value={editValues.status} onValueChange={(v) => setEditValues({ ...editValues, status: v })}>
          <SelectTrigger className="h-8 rounded-none border-2"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <Input value={editValues.artwork} onChange={(e) => setEditValues({ ...editValues, artwork: e.target.value })} placeholder="Agenda" className="h-8 rounded-none border-2" />
      </TableCell>
      <TableCell>
        <Input value={editValues.vendor_name} onChange={(e) => setEditValues({ ...editValues, vendor_name: e.target.value })} placeholder="Vendor" className="h-8 rounded-none border-2" />
      </TableCell>
      <TableCell>
        <Input type="date" value={editValues.ship_date} onChange={(e) => setEditValues({ ...editValues, ship_date: e.target.value })} className="h-8 rounded-none border-2" />
      </TableCell>
      <TableCell>
        <Input type="date" value={editValues.delivery_date} onChange={(e) => setEditValues({ ...editValues, delivery_date: e.target.value })} className="h-8 rounded-none border-2" />
      </TableCell>
      <TableCell>
        <Input value={editValues.order_number} onChange={(e) => setEditValues({ ...editValues, order_number: e.target.value })} placeholder="Order #" className="h-8 rounded-none border-2" />
      </TableCell>
      <TableCell>
        <Input value={editValues.event_code} onChange={(e) => setEditValues({ ...editValues, event_code: e.target.value })} placeholder="Event code" className="h-8 rounded-none border-2" />
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button data-testid={`ship-save-${id}`} onClick={save} size="sm" className="h-8 bg-[var(--color-primary)] text-white rounded-none"><Check className="h-3 w-3" /></Button>
          <Button data-testid={`ship-cancel-${id}`} onClick={cancelEdit} size="sm" variant="outline" className="h-8 rounded-none border-2"><X className="h-3 w-3" /></Button>
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div data-testid="shipments-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Truck className="h-7 w-7" /> Shipments
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Track artworks shipping to venues. Add/edit manually or push via API.
          </p>
        </div>
        <Button data-testid="add-shipment-btn" onClick={startNew} disabled={editingId !== null} className="bg-[var(--color-primary)] text-white rounded-none">
          <Plus className="h-4 w-4 mr-2" /> Add Shipment
        </Button>
      </div>

      <div className="border-2 border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">Tracking #</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Artwork</TableHead>
              <TableHead className="font-semibold">Vendor</TableHead>
              <TableHead className="font-semibold">Ship Date</TableHead>
              <TableHead className="font-semibold">Delivery Date</TableHead>
              <TableHead className="font-semibold">Order #</TableHead>
              <TableHead className="font-semibold">Event Code</TableHead>
              <TableHead className="font-semibold w-28">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {editingId === "NEW" && renderEditRow("NEW", "new-row")}

            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">Loading...</TableCell></TableRow>
            ) : rows.length === 0 && editingId !== "NEW" ? (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-12">
                No shipments yet. Click &quot;Add Shipment&quot; or push some via the API.
              </TableCell></TableRow>
            ) : (
              rows.map(row => editingId === row.id ? renderEditRow(row.id, row.id) : (
                <TableRow key={row.id} data-testid={`ship-row-${row.id}`}>
                  <TableCell className="font-mono text-sm">{row.tracking_number || "-"}</TableCell>
                  <TableCell>
                    <span className={`inline-block px-2 py-1 text-xs font-medium border ${STATUS_BADGE_COLOR[row.status] || "bg-zinc-100 text-zinc-700 border-zinc-300"}`}>
                      {row.status}
                    </span>
                  </TableCell>
                  <TableCell>{row.artwork || "-"}</TableCell>
                  <TableCell>{row.vendor_name || "-"}</TableCell>
                  <TableCell className="text-sm">{row.ship_date ? row.ship_date.slice(0, 10) : "-"}</TableCell>
                  <TableCell className="text-sm">{row.delivery_date ? row.delivery_date.slice(0, 10) : "-"}</TableCell>
                  <TableCell className="text-sm font-mono">{row.order_number || "-"}</TableCell>
                  <TableCell className="text-sm">{row.event_code || "-"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button data-testid={`ship-edit-${row.id}`} onClick={() => startEdit(row)} size="sm" variant="outline" className="h-8 rounded-none border-2"><Edit2 className="h-3 w-3" /></Button>
                      <Button data-testid={`ship-delete-${row.id}`} onClick={() => deleteRow(row.id, row.tracking_number)} size="sm" variant="outline" className="h-8 rounded-none border-2 text-red-600 hover:bg-red-50"><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default ShipmentsTable;
